/**
 * Button Component - Reusable button for feature pages
 */

import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useFeatureTheme } from '../../../theme/hooks/useFeatureTheme';

const ALIAS_VARIANTS = {
  outlined: 'outline',
  outlinedInverse: 'outlineInverse',
};

const Button = React.forwardRef(
  (
    {
      variant = 'primary',
      size = 'md',
      type = 'button',
      disabled = false,
      loading = false,
      fullWidth = false,
      children,
      icon,
      iconPosition = 'left',
      'aria-label': ariaLabel,
      onClick,
      className = '',
      sx = {},
      ...props
    },
    ref
  ) => {
    const { semantic, controlHeights, radiusPx, typography } = useFeatureTheme();
    const resolvedVariant = ALIAS_VARIANTS[variant] || variant;

    const variantStyles = {
      primary: {
        backgroundColor: semantic.interactive.primary,
        color: semantic.interactive.primaryForeground,
        border: `1px solid ${semantic.interactive.primary}`,
        '&:hover:not(:disabled)': {
          backgroundColor: semantic.interactive.primaryHover,
          borderColor: semantic.interactive.primaryHover,
        },
        '&:active:not(:disabled)': {
          backgroundColor: semantic.interactive.primaryActive,
          borderColor: semantic.interactive.primaryActive,
          transform: 'translateY(1px)',
        },
      },
      secondary: {
        backgroundColor: semantic.surfaces.surfaceMuted,
        color: semantic.text.primary,
        border: `1px solid ${semantic.borders.border}`,
        '&:hover:not(:disabled)': {
          backgroundColor: semantic.surfaces.surface,
          borderColor: semantic.borders.borderStrong,
        },
      },
      outline: {
        backgroundColor: 'transparent',
        color: semantic.interactive.primary,
        border: `1px solid ${semantic.borders.border}`,
        '&:hover:not(:disabled)': {
          backgroundColor: semantic.interactive.primarySoft,
          borderColor: semantic.interactive.primary,
        },
      },
      outlineInverse: {
        backgroundColor: 'transparent',
        color: semantic.text.inverse,
        border: `1px solid ${semantic.text.inverse}`,
        '&:hover:not(:disabled)': {
          backgroundColor: 'rgba(255, 255, 255, 0.12)',
        },
      },
      ghost: {
        backgroundColor: 'transparent',
        color: semantic.text.primary,
        border: 'none',
        '&:hover:not(:disabled)': {
          backgroundColor: semantic.surfaces.surfaceMuted,
        },
      },
      destructive: {
        backgroundColor: semantic.states.danger,
        color: semantic.interactive.primaryForeground,
        border: `1px solid ${semantic.states.danger}`,
        '&:hover:not(:disabled)': {
          backgroundColor: `${semantic.states.danger}dd`,
        },
        '&:focus-visible': {
          boxShadow: `0 0 0 3px ${semantic.states.dangerSoft}`,
        },
      },
      link: {
        backgroundColor: 'transparent',
        color: semantic.interactive.primary,
        border: 'none',
        padding: '0',
        textDecoration: 'none',
        '&:hover:not(:disabled)': {
          color: semantic.interactive.primaryHover,
          textDecoration: 'underline',
        },
      },
      icon: {
        backgroundColor: semantic.surfaces.surfaceMuted,
        color: semantic.text.primary,
        border: `1px solid ${semantic.borders.border}`,
        '&:hover:not(:disabled)': {
          backgroundColor: semantic.surfaces.surface,
          borderColor: semantic.borders.borderStrong,
        },
      },
    };

    const sizeStyles = {
      sm: {
        height: controlHeights.sm,
        padding: '0 12px',
        fontSize: typography.fontSizeSm,
        fontWeight: typography.fontWeightMedium,
      },
      md: {
        height: controlHeights.md,
        padding: '0 16px',
        fontSize: typography.fontSizeMd,
        fontWeight: typography.fontWeightMedium,
      },
      lg: {
        height: controlHeights.lg,
        padding: '0 20px',
        fontSize: typography.fontSizeBase,
        fontWeight: typography.fontWeightSemibold,
      },
      icon: {
        height: controlHeights.md,
        width: controlHeights.md,
        padding: 0,
      },
    };

    const computedSize = resolvedVariant === 'icon' ? 'icon' : size;
    const styles = variantStyles[resolvedVariant] || variantStyles.primary;
    const isDisabled = disabled || loading;

    const disabledVariantStyles = {
      primary: {
        backgroundColor:
          semantic.inputs?.backgroundDisabled || semantic.surfaces.surfaceMuted,
        color: semantic.text.muted,
        border: `1px solid ${semantic.borders.border}`,
      },
      secondary: {
        backgroundColor:
          semantic.inputs?.backgroundDisabled || semantic.surfaces.surfaceMuted,
        color: semantic.text.muted,
        border: `1px solid ${semantic.borders.border}`,
      },
      outline: {
        backgroundColor: 'transparent',
        color: semantic.text.muted,
        border: `1px solid ${semantic.borders.border}`,
      },
      outlineInverse: {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: 'rgba(255, 255, 255, 0.5)',
        border: '1px solid rgba(255, 255, 255, 0.24)',
      },
      ghost: {
        backgroundColor: 'transparent',
        color: semantic.text.muted,
        border: 'none',
      },
      destructive: {
        backgroundColor:
          semantic.inputs?.backgroundDisabled || semantic.surfaces.surfaceMuted,
        color: semantic.text.muted,
        border: `1px solid ${semantic.borders.border}`,
      },
      link: {
        backgroundColor: 'transparent',
        color: semantic.text.muted,
        border: 'none',
      },
      icon: {
        backgroundColor:
          semantic.inputs?.backgroundDisabled || semantic.surfaces.surfaceMuted,
        color: semantic.text.muted,
        border: `1px solid ${semantic.borders.border}`,
      },
    };

    const resolvedStyles =
      isDisabled && !loading
        ? {
            ...styles,
            ...(disabledVariantStyles[resolvedVariant] || disabledVariantStyles.primary),
            '&:hover:not(:disabled)': {},
            '&:active:not(:disabled)': {},
          }
        : styles;

    return (
      <Box
        ref={ref}
        component="button"
        type={type}
        onClick={onClick}
        disabled={isDisabled}
        aria-label={ariaLabel}
        aria-busy={loading || undefined}
        className={className}
        sx={{
          ...resolvedStyles,
          ...sizeStyles[computedSize],
          borderRadius: radiusPx.md,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: fullWidth ? '100%' : 'auto',
          gap: '8px',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          opacity: loading ? 0.85 : 1,
          pointerEvents: isDisabled ? 'none' : 'auto',
          transition: 'all 160ms ease',
          fontFamily: 'inherit',
          whiteSpace: 'nowrap',
          '&:focus-visible': resolvedStyles['&:focus-visible'] || styles['&:focus-visible'] || {
            outline: 'none',
            boxShadow: `0 0 0 3px ${semantic.borders.focusRing}`,
          },
          ...sx,
        }}
        {...props}
      >
        {loading ? (
          <CircularProgress size={16} color="inherit" aria-hidden="true" />
        ) : (
          <>
            {icon && iconPosition === 'left' && icon}
            {children}
            {icon && iconPosition === 'right' && icon}
          </>
        )}
      </Box>
    );
  }
);

Button.displayName = 'Button';

export default Button;
